import {
  registerApplication,
  start,
  addErrorHandler,
  getAppStatus,
  LOAD_ERROR,
  MOUNTED,
  type AppError,
} from "single-spa";
import type { ShellHandle } from "@observe-tech/mfe-shell-api";
import type { MfeErrorApi } from "../shell-handle/mfeError";
import { recoverDevMfe } from "./mfeRecovery";
import {
  refreshLocalServers,
  akvaScannerBackendUrl,
} from "../shared/localServers";
import { isStageOrDev } from "../shared/hooks/useIsStageOrDev";
import {
  createMfeStyleIsolation,
  withStyleIsolation,
} from "./mfeStyleIsolation";

// Live-mfe dev tracing only. `__MARLIN_DESKTOP_DEV__` is injected into index.html
// by the desktop dev flow (appProtocol.ts) and is absent in every deployed
// (prod/stage browser) build, so these are no-ops there.
const desktopDevFlag: unknown = Reflect.get(
  globalThis,
  "__MARLIN_DESKTOP_DEV__",
);
export const DESKTOP_DEV = desktopDevFlag === true;
const dlog = (...a: unknown[]) => {
  if (DESKTOP_DEV) console.info(...a);
};
const derr = (...a: unknown[]) => {
  if (DESKTOP_DEV) console.error(...a);
};

const SHELL_OWNED_PREFIXES = [
  "/login",
  "/logout",
  "/welcome",
  "/access-denied",
  "/not-found",
  "/test-mfe",
];
const SHELL_OWNED_EXACT = new Set(["/", "/login/callback"]);
const TIDE_PREFIX = "/tide";
const AKVA_PREFIX = "/akva-analytics";
const AKVA_NETWORK_SCANNER_PREFIX = "/akva-network-scanner";
const MARLIN_V2_PREFIX = "/marlin-v2";
const MANUAL_PREFIX = "/manual";
const SUBMERGED_PREFIX = "/submerged";

export function isShellOwnedPath(pathname: string): boolean {
  if (SHELL_OWNED_EXACT.has(pathname)) return true;
  return SHELL_OWNED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

export interface MfeInfo {
  framework: string;
  version: string;
  endpoints?: Record<string, string>;
}

const mfeInfoRegistry: Record<string, MfeInfo> = {};
const mfeInfoSubs = new Set<() => void>();

export function getMfeInfo(name: string): MfeInfo | undefined {
  return mfeInfoRegistry[name];
}

export function subscribeMfeInfo(cb: () => void): () => void {
  mfeInfoSubs.add(cb);
  return () => {
    mfeInfoSubs.delete(cb);
  };
}

// Record an app's framework/version. Called by the MFE loader (from each MFE's
// self-reported `mfeInfo` export) and by the shell for its own runtime version.
// There is no hardcoded fallback table — anything not reported reads as unknown.
export function reportMfeInfo(name: string, info: MfeInfo): void {
  const current = mfeInfoRegistry[name];
  if (
    current?.framework === info.framework &&
    current?.version === info.version &&
    JSON.stringify(current?.endpoints) === JSON.stringify(info.endpoints)
  ) {
    return;
  }
  mfeInfoRegistry[name] = {
    framework: String(info.framework),
    version: String(info.version),
    ...(info.endpoints ? { endpoints: info.endpoints } : {}),
  };
  mfeInfoSubs.forEach((cb) => cb());
}

// Wrap a single-spa import loader so we can read the MFE's self-reported
// { mfeInfo: { framework, version } } export when it loads.
function loadMfe(name: string, url: string) {
  return async () => {
    dlog(`[shell] loadMfe START ${name} <- ${url}`);
    try {
      const mod = (await import(/* @vite-ignore */ url)) as {
        mfeInfo?: MfeInfo;
      };
      dlog(
        `[shell] loadMfe OK ${name}`,
        mod?.mfeInfo,
        "exports:",
        Object.keys(mod || {}),
      );
      if (mod?.mfeInfo) reportMfeInfo(name, mod.mfeInfo);
      return mod;
    } catch (err) {
      derr(`[shell] loadMfe FAILED ${name} <- ${url}`, err);
      throw err;
    }
  };
}

// Per-MFE runtime override map injected before the shell bundle runs (desktop
// dev: `./observe --marlin -d -f <mfe>`). Takes precedence over the build-time
// VITE_*_MFE_URL value so a single MFE can be served live from a local Vite dev
// server while everything else loads from the deployed baseline. Absent in
// normal builds, so behaviour is unchanged.
// Where an MFE's code is being loaded from, for the debug panel:
//  - live=true  → a dev override / explicit VITE_*_MFE_URL (a localhost dev-server
//    port with hot rebuilds);
//  - live=false → the same-origin baseline path served from the S3 sync.
export interface MfeSource {
  url: string;
  live: boolean;
}
const mfeSourceRegistry: Record<string, MfeSource> = {};

export function getMfeSource(name: string): MfeSource | undefined {
  return mfeSourceRegistry[name];
}

function resolveMfeUrl(
  appName: string,
  key: string,
  envUrl: string | undefined,
  fallback?: string,
): string | undefined {
  const override = globalThis.__MFE_DEV_OVERRIDES__?.[key];
  const url = override || envUrl || fallback;
  dlog(
    `[shell] resolveMfeUrl ${appName} key=${key} override=${override ?? "-"} env=${envUrl ?? "-"} fallback=${fallback ?? "-"} => ${url ?? "(none)"}`,
  );
  if (url) {
    mfeSourceRegistry[appName] = { url, live: Boolean(override || envUrl) };
  }
  return url;
}

const FALLBACK_CONTAINER_ATTR = "data-shell-fallback-container";

function mfeContainers(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>("#single-spa-app-container"),
  );
}

// The chrome node (__root.tsx) is the real mount point, but registration and
// the login route both run without it, so a fallback is appended to the body.
// It must not survive: an MFE sheet targeting the id (akva sets
// min-height:100vh) matches the leftover too, and a viewport-tall empty block
// in body flow makes the whole document scroll.
export function dropFallbackMfeContainer(): void {
  for (const el of mfeContainers()) {
    if (el.hasAttribute(FALLBACK_CONTAINER_ATTR) && !el.hasChildNodes()) {
      el.remove();
    }
  }
}

export function getMfeContainer(): HTMLElement {
  const found = mfeContainers();
  const owned = found.find((el) => !el.hasAttribute(FALLBACK_CONTAINER_ATTR));
  if (owned) {
    for (const el of found) {
      if (el !== owned && !el.hasChildNodes()) el.remove();
    }
    return owned;
  }
  if (found[0]) return found[0];
  const fallback = document.createElement("div");
  fallback.id = "single-spa-app-container";
  fallback.setAttribute(FALLBACK_CONTAINER_ATTR, "");
  document.body.appendChild(fallback);
  return fallback;
}

let registered = false;
let started = false;
let errorHandlerRegistered = false;

export function attachMfeErrorHandler(mfeError: MfeErrorApi) {
  if (errorHandlerRegistered) return;
  errorHandlerRegistered = true;
  addErrorHandler((err: AppError) => {
    const appName =
      typeof err.appOrParcelName === "string"
        ? err.appOrParcelName
        : "unknown-mfe";
    console.error("[shell] MFE error", appName, err);
    mfeError.set(appName, err);
    // A rejected load parks the app in LOAD_ERROR, which single-spa retries on
    // the next route change; in live-mfe dev the browser's cached module
    // failure means only a reload actually recovers (see mfeRecovery.ts).
    if (DESKTOP_DEV && getAppStatus(appName) === LOAD_ERROR) {
      const src = mfeSourceRegistry[appName];
      if (src) void recoverDevMfe(src.url);
    }
  });
  // The overlay stays up until the failed app mounts again (single-spa retries
  // LOAD_ERROR apps on the next route change), then clears itself.
  window.addEventListener("single-spa:app-change", () => {
    const current = mfeError.state.get();
    if (current && getAppStatus(current.appName) === MOUNTED) mfeError.clear();
  });
}

export function registerMfes(handle: ShellHandle) {
  if (registered) return;
  registered = true;
  dlog(
    "[shell] registerMfes; overrides=",
    JSON.stringify(globalThis.__MFE_DEV_OVERRIDES__),
  );

  // Load the Electron local-servers map so the scanner backend URL can be
  // resolved synchronously in customProps below.
  void refreshLocalServers();

  const customProps = () => ({
    shellHandle: handle,
    domElement: getMfeContainer(),
    domElementGetter: () => getMfeContainer(),
    // Backend host for the akva-network-scanner MFE: the machine chosen in the
    // topbar local-server picker (its controller IP+port + the proxy path).
    // Resolved at mount; re-selecting a machine remounts the MFE. Other MFEs
    // ignore this prop.
    akvaBackendUrl: akvaScannerBackendUrl(),
    // Auth for the controller proxy route the scanner backend now sits behind.
    // The proxy is gated by the controller AuthGuard; the static frontend key
    // maps to the OBSERVE role (same key tide uses as REACT_APP_MARLIN_LOCAL_AUTH).
    akvaAuthToken: import.meta.env.VITE_MARLIN_LOCAL_AUTH,
  });

  const tideUrl = resolveMfeUrl(
    "@observe/tide",
    "tide",
    import.meta.env.VITE_TIDE_MFE_URL,
    "/tide/mfe-entry.js",
  );
  if (tideUrl) {
    registerApplication({
      name: "@observe/tide",
      app: loadMfe("@observe/tide", tideUrl),
      activeWhen: (loc) => loc.pathname.startsWith(TIDE_PREFIX),
      customProps,
    });
  }

  const akvaUrl = resolveMfeUrl(
    "@observe/akva-analytics",
    "akva-analytics",
    import.meta.env.VITE_AKVA_MFE_URL,
    "/akva-analytics/mfe-entry.js",
  );
  if (akvaUrl) {
    // Its bundle rewrites what were `body` rules onto #single-spa-app-container
    // itself (`min-height:100vh`, a background, a `*` reset). Those outlive the
    // unmount and then apply to every other remote, so the sheet is switched
    // off while the app is not mounted. See mfeStyleIsolation.ts.
    registerApplication({
      name: "@observe/akva-analytics",
      app: withStyleIsolation(
        loadMfe("@observe/akva-analytics", akvaUrl),
        createMfeStyleIsolation(document, akvaUrl),
      ),
      activeWhen: (loc) => loc.pathname.startsWith(AKVA_PREFIX),
      customProps,
    });
  }

  const akvaScannerUrl = resolveMfeUrl(
    "@observe/akva-network-scanner",
    "akva-network-scanner",
    import.meta.env.VITE_AKVA_NETWORK_SCANNER_MFE_URL,
    "/akva-network-scanner/mfe-entry.js",
  );
  if (akvaScannerUrl) {
    // The scanner is a Tailwind 3 bundle: its injected stylesheet is unlayered
    // and therefore outranks every Tailwind 4 utility the other MFEs emit, so
    // its CSS has to be switched off while it is not mounted. See
    // mfeStyleIsolation.ts.
    registerApplication({
      name: "@observe/akva-network-scanner",
      app: withStyleIsolation(
        loadMfe("@observe/akva-network-scanner", akvaScannerUrl),
        createMfeStyleIsolation(document, akvaScannerUrl),
      ),
      activeWhen: (loc) => loc.pathname.startsWith(AKVA_NETWORK_SCANNER_PREFIX),
      customProps,
    });
  }

  const marlinV2Url = resolveMfeUrl(
    "@observe/marlin-v2",
    "marlin-v2",
    import.meta.env.VITE_MARLIN_V2_MFE_URL,
    "/marlin-v2/mfe-entry.js",
  );
  if (marlinV2Url) {
    registerApplication({
      name: "@observe/marlin-v2",
      app: loadMfe("@observe/marlin-v2", marlinV2Url),
      activeWhen: (loc) => loc.pathname.startsWith(MARLIN_V2_PREFIX),
      customProps,
    });
  }

  const manualUrl = resolveMfeUrl(
    "@observe/manual",
    "manual",
    import.meta.env.VITE_MANUAL_MFE_URL,
    "/manual/mfe-entry.js",
  );
  // Customers only reach the manual from the desktop app, but staging and local
  // dev always register it so it is reachable — and listed in the debug badge —
  // in the browser shell too.
  if (manualUrl && (window.electronAPI || isStageOrDev())) {
    registerApplication({
      name: "@observe/manual",
      app: loadMfe("@observe/manual", manualUrl),
      activeWhen: (loc) => loc.pathname.startsWith(MANUAL_PREFIX),
      customProps,
    });
  }

  const submergedUrl = resolveMfeUrl(
    "@observe/submerged",
    "submerged",
    import.meta.env.VITE_SUBMERGED_MFE_URL,
  );
  if (submergedUrl) {
    registerApplication({
      name: "@observe/submerged",
      app: loadMfe("@observe/submerged", submergedUrl),
      activeWhen: (loc) => loc.pathname.startsWith(SUBMERGED_PREFIX),
      customProps,
    });
  }
}

export function startSingleSpa() {
  if (started) return;
  started = true;
  dlog(
    "[shell] startSingleSpa at",
    window.location.pathname + window.location.search,
  );
  start({ urlRerouteOnly: true });
}
